import React, { useEffect, useState, useRef } from 'react';
import { Badge, List, Typography, Space, Empty, Drawer } from 'antd';
import { 
  ThunderboltOutlined, 
  PhoneOutlined, 
  TeamOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import wsService from '@/services/websocket';
import { getESTDate } from '@/utils/format';

const { Text } = Typography;

interface LiveEvent {
  id: string;
  time: string;
  type: string;
  kind: string;
  message: string;
  color: string;
  icon: React.ReactNode;
  data?: any;
}

const LiveEventFeed: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [events, setEvents] = useState<LiveEvent[]>([]);

  useEffect(() => {
    const handleEvent = (message: any) => {
      const { type, data } = message;
      if (!data || !data.kind) return;

      const kind = data.kind;
      let eventIcon = <ThunderboltOutlined />;
      let eventColor = '#6f42c1';
      let eventMsg = `Event: ${kind}`;

      switch (kind) {
        case 'call.ringing':
          eventIcon = <PhoneOutlined />;
          eventColor = '#1890ff';
          eventMsg = `Incoming call from ${data.call?.from || 'Unknown'}`;
          break;
        case 'call.routing':
          eventIcon = <TeamOutlined />;
          eventColor = '#722ed1';
          eventMsg = `Routing to available CSRs...`;
          break;
        case 'call.assigned':
          eventIcon = <CustomerServiceOutlined />;
          eventColor = '#52c41a';
          eventMsg = `Call assigned to Agent #${data.toCsrId}`;
          break;
        case 'call.held':
          eventIcon = <ClockCircleOutlined />;
          eventColor = '#faad14';
          eventMsg = `Call placed on public hold`;
          break;
        case 'call.completed':
        case 'call.ended':
          eventIcon = <CheckCircleOutlined />;
          eventColor = '#8c8c8c';
          eventMsg = `Call ended session`;
          break;
        case 'queue.updated':
          eventIcon = <PhoneOutlined />;
          eventColor = data.origin ? '#fa8c16' : '#13c2c2';
          eventMsg = data.origin
            ? `AI queued a callback for ${data.customerName || data.customerPhone || 'a customer'}`
            : `Callback queue entry #${data.queueId} updated`;
          break;
        case 'queue.deleted':
          eventIcon = <CheckCircleOutlined />;
          eventColor = '#8c8c8c';
          eventMsg = `Callback queue entry #${data.queueId} removed`;
          break;
      }

      const newEvent: LiveEvent = {
        id: Math.random().toString(36).substr(2, 9),
        time: getESTDate().format('HH:mm:ss'),
        type,
        kind,
        message: eventMsg,
        color: eventColor,
        icon: eventIcon,
        data,
      };

      setEvents(prev => [newEvent, ...prev].slice(0, 50));
    };

    wsService.on('all', handleEvent);
    return () => wsService.off('all', handleEvent);
  }, []);

  return (
    <Drawer
      title={
        <Space>
           <GlobalOutlined style={{ color: '#52c41a' }} />
           <span>Live System Pulse</span>
           <Badge status="processing" color="#52c41a" />
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      styles={{ body: { padding: '0px' } }}
    >
      {events.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
           <Empty description="Waiting for live system activity..." />
        </div>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={events}
          renderItem={(item) => (
            <List.Item style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
               <List.Item.Meta
                 avatar={
                   <div style={{ 
                     background: `${item.color}1A`, 
                     color: item.color, 
                     width: '36px', 
                     height: '36px', 
                     borderRadius: '8px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     fontSize: '18px'
                   }}>
                     {item.icon}
                   </div>
                 }
                 title={
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Text strong style={{ fontSize: '12px', color: item.color }}>{item.kind.toUpperCase()}</Text>
                     <Text type="secondary" style={{ fontSize: '11px' }}>{item.time}</Text>
                   </div>
                 }
                 description={
                   <div>
                     <Text style={{ fontSize: '13px' }}>{item.message}</Text>
                     {item.data?.call?.callSid && (
                       <div style={{ marginTop: '4px' }}>
                         <Text type="secondary" style={{ fontSize: '10px' }}>SID: {item.data.call.callSid.substring(0, 15)}...</Text>
                       </div>
                     )}
                   </div>
                 }
               />
            </List.Item>
          )}
        />
      )}
      <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #eee', background: '#fafafa' }}>
        <Text type="secondary" style={{ fontSize: '11px' }}>Monitoring {events.length} recent background operations</Text>
      </div>
    </Drawer>
  );
};

export default LiveEventFeed;
